import { useQuery } from "@tanstack/react-query";
import { from, unwrap } from "@vixio/data";
import type { StatusMap } from "@vixio/ui";

export type CaseStatus = "open" | "negotiating" | "agreement" | "paid" | "lost" | "canceled";
export const CASE_STATUS: StatusMap = {
  open: { label: "em aberto", tone: "crit" },
  negotiating: { label: "negociando", tone: "warn" },
  agreement: { label: "em acordo", tone: "info" },
  paid: { label: "pago", tone: "ok" },
  lost: { label: "perda", tone: "neutral" },
  canceled: { label: "cancelado", tone: "neutral" },
};
export const ACTIVE_CASE: CaseStatus[] = ["open", "negotiating"];

export type AgreementStatus = "active" | "fulfilled" | "broken" | "canceled";
export const AGREEMENT_STATUS: StatusMap = {
  active: { label: "vigente", tone: "ok" },
  fulfilled: { label: "cumprido", tone: "ok" },
  broken: { label: "quebrado", tone: "crit" },
  canceled: { label: "cancelado", tone: "neutral" },
};

export const CHANNELS = { whatsapp: "WhatsApp", email: "E-mail", manual: "Manual" } as const;
export type Channel = keyof typeof CHANNELS;

export const EVENT_LABEL: Record<string, string> = {
  opened: "Caso aberto",
  step_due: "Passo da régua disparado",
  contact: "Contato registrado",
  promise: "Promessa de pagamento",
  agreement: "Entrou em acordo",
  paid: "Parcela quitada",
  closed: "Caso encerrado",
  reopened: "Caso reaberto",
};

export interface CaseRow {
  id: string;
  org_id: string;
  installment_id: string;
  receivable_id: string;
  issuer_id: string | null;
  person_id: string | null;
  counterparty_name: string;
  due_date: string;
  original_cents: number;
  open_cents: number;
  status: CaseStatus;
  last_step_id: string | null;
  last_contact_at: string | null;
  promise_date: string | null;
  promise_cents: number | null;
  opened_at: string;
  closed_at: string | null;
  close_reason: string | null;
}

export interface CaseEvent {
  id: number;
  case_id: string;
  kind: string;
  channel: string | null;
  step_id: string | null;
  note: string | null;
  data: Record<string, unknown>;
  created_at: string;
}

export interface Agreement {
  id: string;
  org_id: string;
  number: string;
  person_id: string | null;
  counterparty_name: string;
  status: AgreementStatus;
  debt_cents: number;
  discount_pct: number;
  down_payment_cents: number;
  installments: number;
  first_due: string;
  agreed_cents: number;
  receivable_id: string | null;
  note: string | null;
  status_reason: string | null;
  created_at: string;
}

export interface RuleStep {
  id: string;
  code: string;
  offset_days: number;
  channel: Channel;
  template_key: string;
  template_text: string;
  min_open_cents: number;
  sort: number;
  active: boolean;
}

export interface Band {
  id: string;
  label: string;
  from_days: number;
  to_days: number | null;
  pct: number;
  active: boolean;
}

export interface ProvisionRow {
  band_id: string;
  label: string;
  from_days: number;
  to_days: number | null;
  pct: number;
  cases: number;
  open_cents: number;
  provision_cents: number;
}

export interface CollectionsSettings {
  active: boolean;
  grace_days: number;
  min_open_cents: number;
  allow_agreements: boolean;
  agreement_max_installments: number;
  agreement_max_discount_pct: number;
  agreement_min_down_pct: number;
}

type U = string;
declare module "@vixio/data" {
  interface RpcRegistry {
    "collections.set_settings": { args: { p_org: U; p_patch: Partial<CollectionsSettings> }; returns: CollectionsSettings };
    "collections.install_defaults": { args: { p_org: U }; returns: number };
    "collections.upsert_step": {
      args: {
        p_org: U; p_code: string; p_offset_days: number; p_channel: Channel; p_template_key: string; p_template_text: string;
        p_min_open_cents?: number; p_sort?: number; p_active?: boolean;
      };
      returns: U;
    };
    "collections.upsert_provision_band": {
      args: { p_org: U; p_label: string; p_from_days: number; p_to_days: number | null; p_pct: number; p_active?: boolean };
      returns: U;
    };
    "collections.scan": {
      args: { p_date?: string | null; p_org?: U | null };
      returns: { date: string; opened: number; updated: number; closed: number; steps_due: number; agreements_fulfilled: number };
    };
    "collections.register_contact": { args: { p_case: U; p_channel: Channel; p_note: string }; returns: null };
    "collections.register_promise": { args: { p_case: U; p_date: string; p_cents?: number | null; p_note?: string | null }; returns: null };
    "collections.close_case": { args: { p_case: U; p_status: "lost" | "canceled"; p_reason: string }; returns: null };
    "collections.create_agreement": {
      args: {
        p_org: U; p_case_ids: U[]; p_installments: number; p_first_due: string; p_discount_pct?: number; p_down_payment_cents?: number;
        p_note?: string | null;
      };
      returns: U;
    };
    "collections.set_agreement_status": { args: { p_agreement: U; p_status: "broken" | "canceled"; p_reason: string }; returns: null };
  }
}

export const DEFAULT_SETTINGS: CollectionsSettings = {
  active: true,
  grace_days: 0,
  min_open_cents: 0,
  allow_agreements: true,
  agreement_max_installments: 12,
  agreement_max_discount_pct: 0,
  agreement_min_down_pct: 0,
};

export function useCollectionsSettings(orgId: string) {
  return useQuery({
    queryKey: ["collections", "settings", orgId],
    queryFn: async () => {
      const rows = await unwrap<CollectionsSettings[]>(from("collections", "settings").select("*").eq("org_id", orgId));
      const s = rows[0];
      return s
        ? { ...s, agreement_max_discount_pct: Number(s.agreement_max_discount_pct), agreement_min_down_pct: Number(s.agreement_min_down_pct) }
        : DEFAULT_SETTINGS;
    },
  });
}

export function useSteps(orgId: string) {
  return useQuery({
    queryKey: ["collections", "steps", orgId],
    queryFn: () => unwrap<RuleStep[]>(from("collections", "rule_steps").select("*").eq("org_id", orgId).order("offset_days").order("sort")),
  });
}

export function useProvisions(orgId: string) {
  return useQuery({
    queryKey: ["collections", "provisions", orgId],
    queryFn: async () => {
      const rows = await unwrap<ProvisionRow[]>(from("collections", "v_provisions").select("*").eq("org_id", orgId).order("from_days"));
      return rows.map((r) => ({ ...r, pct: Number(r.pct), cases: Number(r.cases), open_cents: Number(r.open_cents), provision_cents: Number(r.provision_cents) }));
    },
  });
}

export function useCollectionsKpis(orgId: string) {
  return useQuery({
    queryKey: ["collections", "kpis", orgId],
    queryFn: async () => {
      const cases = await unwrap<Array<{ status: CaseStatus; open_cents: number; promise_date: string | null }>>(
        from("collections", "cases").select("status, open_cents, promise_date").eq("org_id", orgId).in("status", ["open", "negotiating", "agreement"]).limit(5000),
      );
      const active = cases.filter((c) => c.status !== "agreement");
      return {
        activeCount: active.length,
        activeCents: active.reduce((a, c) => a + Number(c.open_cents), 0),
        negotiating: cases.filter((c) => c.status === "negotiating").length,
        withPromise: active.filter((c) => c.promise_date).length,
        agreementCount: cases.filter((c) => c.status === "agreement").length,
      };
    },
  });
}
